import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { webImageChooser, WebImageSelection } from "../../atoms/web-image";
import * as FileSystem from "expo-file-system";
import uuid from "react-native-uuid";
import { Image, Platform } from "react-native";
import { useSnackbar } from "../../hooks/snackbar";
import { useI18n } from "../../hooks/i18n-hooks";

type WebImageData = {
    uri: string;
};

export type WebImagePickerResult = {
    canceled: boolean;
    assets?: WebImageData[] | null;
};

const getExtensionForMimeType = (mimeType: string) => {
    switch (mimeType) {
        case "image/jpeg":
            return "jpg";
        case "image/png":
            return "png";
        case "image/webp":
            return "webp";
        case "image/":
            return "jpg";
        default:
            throw new Error("Unsupported image type");
    }
};

export const useWebImagePicker = () => {
    const setWebImageAtom = useSetAtom(webImageChooser);
    const { showSnackbar } = useSnackbar();
    const i18n = useI18n();
    return useCallback(async () => {
        return new Promise<WebImagePickerResult>((resolve) => {
            setWebImageAtom({
                onSelectImage: (selectedImage: WebImageSelection | undefined | null) => {
                    if (!selectedImage) {
                        setWebImageAtom(null);
                        return resolve({ canceled: true, assets: null });
                    }
                    const _asyncFunc = async () => {
                        const newImageId = uuid.v4();
                        const ext = getExtensionForMimeType(selectedImage.mimeType);
                        const imageName = newImageId + "." + ext;

                        let result;

                        try {
                            // download image locally
                            if (Platform.OS === "web") {
                                const response = await fetch(selectedImage.url);
                                const blob = await response.blob();
                                const uri = await blobToBase64(blob);
                                if (uri) {
                                    result = { uri };
                                }
                            } else {
                                const localUri = new FileSystem.File(
                                    FileSystem.Paths.cache,
                                    imageName,
                                );
                                const downloadResult = await FileSystem.File.downloadFileAsync(
                                    selectedImage.url,
                                    localUri,
                                );
                                if (downloadResult.exists) {
                                    result = { uri: downloadResult.uri };
                                }
                            }
                        } catch (_e) {
                            /* empty */
                        }
                        if (result) {
                            resolve({ canceled: false, assets: [{ uri: result.uri }] });
                            setWebImageAtom(null);
                        } else {
                            showSnackbar({ text: i18n.t("webImageDownloadError"), mode: "error" });
                            throw new Error("Error downloading image");
                        }
                    };
                    setWebImageAtom(null);
                    void _asyncFunc();
                },
            });
        });
    }, [i18n, setWebImageAtom, showSnackbar]);
};

const blobToBase64 = (blob: Blob) => {
    return new Promise<string | undefined>((resolve, _) => {
        const reader = new FileReader();
        reader.onloadend = () => (reader.result ? resolve(reader.result as string) : undefined);
        reader.readAsDataURL(blob);
    });
};

export const useImageIsValidImage = () => {
    return useCallback(async (imageUrl: string) => {
        return new Promise<boolean>((resolve) => {
            Image.getSize(
                imageUrl,
                (width, height) => {
                    if (width === 0 || height === 0) {
                        resolve(false);
                    } else {
                        // success
                        resolve(true);
                    }
                },
                () => {
                    // failure
                    resolve(false);
                },
            );
        });
    }, []);
};
